import type { LegalDoc, LegalDocVersion } from '@/mocks/legalCenter';
import { getAllVersions } from '@/mocks/legalCenter';

interface HistoryModalProps {
  doc: LegalDoc;
  onClose: () => void;
  onActivateVersion: (docId: number, versionNumber: number) => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const statusBadge: Record<string, { label: string; dot: string; bg: string; text: string }> = {
  active: { label: 'Activa', dot: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  draft: { label: 'Borrador', dot: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
  archived: { label: 'Archivada', dot: 'bg-stone-400', bg: 'bg-stone-50', text: 'text-stone-600' },
};

export default function HistoryModal({ doc, onClose, onActivateVersion }: HistoryModalProps) {
  const versions = getAllVersions(doc);
  const hasDraft = versions.some((v) => v.status === 'draft');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      <div className="relative z-10 bg-background-50 rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center">
              <i className="ri-history-line text-foreground-600 text-sm"></i>
            </div>
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">Historial de Versiones</h3>
              <p className="text-foreground-400 text-[10px]">{doc.title} · {versions.length} versiones</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[15px] top-0 bottom-0 w-0.5 bg-background-200"></div>

            <div className="space-y-4">
              {versions.map((version, idx) => {
                const badge = statusBadge[version.status];
                const isFirst = idx === 0;
                const isActive = version.status === 'active';
                const isDraft = version.status === 'draft';

                return (
                  <div key={version.versionNumber} className="relative pl-10">
                    {/* Dot */}
                    <div className={`absolute left-[11px] top-1.5 w-[9px] h-[9px] rounded-full border-2 border-background-50 z-10 ${
                      isActive
                        ? 'bg-emerald-500'
                        : isDraft
                        ? 'bg-amber-500'
                        : 'bg-stone-300'
                    }`}></div>

                    {/* Card */}
                    <div className={`rounded-xl border p-4 ${
                      isActive
                        ? 'border-emerald-200 bg-emerald-50/50'
                        : isDraft
                        ? 'border-amber-200 bg-amber-50/50'
                        : 'border-background-200 bg-background-50'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-foreground-800 text-xs font-bold">v{version.versionNumber}</span>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${badge.bg} ${badge.text}`}>
                            <span className={`w-1 h-1 rounded-full ${badge.dot}`}></span>
                            {badge.label}
                          </span>
                        </div>
                        {isDraft && (
                          <button
                            onClick={() => onActivateVersion(doc.id, version.versionNumber)}
                            className="px-2.5 py-1 rounded-lg bg-emerald-500 text-white text-[9px] font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Activar
                          </button>
                        )}
                      </div>

                      <p className="text-foreground-600 text-[10px] leading-relaxed mb-2 line-clamp-3">
                        {version.content.replace(/<[^>]+>/g, ' ').substring(0, 200)}{version.content.length > 200 ? '...' : ''}
                      </p>

                      <div className="flex items-center gap-3 text-foreground-400 text-[9px]">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line text-[9px]"></i>
                          {version.createdBy}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line text-[9px]"></i>
                          {formatDate(version.createdAt)}
                        </span>
                      </div>

                      {version.notes && (
                        <div className="mt-2 pt-2 border-t border-background-200/70">
                          <p className="text-foreground-400 text-[9px] italic">{version.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 py-4 border-t border-background-200 bg-background-100/50">
          <div className="flex items-center justify-between">
            <p className="text-foreground-400 text-[10px]">
              {hasDraft ? 'Hay un borrador pendiente de activación.' : 'Todas las versiones están archivadas o activas.'}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-background-200 text-foreground-600 text-xs font-medium hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}